// ---------------------------------------------------------------------------
// brendaPrices — pricing helpers
// ---------------------------------------------------------------------------
//
// Pure price-derivation functions for a Brenda charges response. These
// are fresh copies of the equivalents in `app/utils/product.ts` so the
// new charges flow can live without reaching into the old helpers, and
// so price rules can evolve here without affecting legacy code paths.
// ---------------------------------------------------------------------------

// Types
import type { ItemTotals, PriceValue } from '~/types'

// ---------------------------------------------------------------------------
// Rounding
// ---------------------------------------------------------------------------

/** Rounds a number to `places` decimal places without float drift. */
const roundToPlaces = (value: number, places: number): number => {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

// ---------------------------------------------------------------------------
// Product price
// ---------------------------------------------------------------------------

/**
 * Computes the inc-VAT product price from `itemTotals`.
 *
 * - `bulk` (hire)      → `hireTotal` + `hireVat`
 * - `consumable` (buy) → `resaleTotal` + `resaleVat`
 *
 * Returns `null` when the relevant totals aren't present. Note that for
 * the main item these totals are already quantity-adjusted by Brenda;
 * for linked / cross-sell products they are per-unit.
 */
export const getBrendaProductPrice = (
  totals: ItemTotals | null | undefined,
  productType: 'bulk' | 'consumable'
): PriceValue | null => {
  if (!totals) return null

  if (productType === 'bulk') {
    const total = totals.hireTotal?.amount
    const vat = totals.hireVat?.amount
    if (total == null || vat == null) return null
    return {
      currency: totals.hireTotal?.currency ?? 'GBP',
      amount: roundToPlaces(total + vat, 2),
    }
  }

  const total = totals.resaleTotal?.amount
  const vat = totals.resaleVat?.amount
  if (total == null || vat == null) return null
  return {
    currency: totals.resaleTotal?.currency ?? 'GBP',
    amount: roundToPlaces(total + vat, 2),
  }
}

/**
 * Returns the VAT portion of the product price from `itemTotals`.
 *
 * - `bulk` (hire)      → `hireVat`
 * - `consumable` (buy) → `resaleVat`
 *
 * Used alongside `getBrendaProductPrice` (which returns inc-VAT) to
 * split a line into ex-VAT + VAT components for summary displays.
 * Returns `null` when the relevant VAT field isn't present.
 */
export const getBrendaProductVat = (
  totals: ItemTotals | null | undefined,
  productType: 'bulk' | 'consumable'
): PriceValue | null => {
  if (!totals) return null

  if (productType === 'bulk') {
    const vat = totals.hireVat?.amount
    if (vat == null) return null
    return {
      currency: totals.hireVat?.currency ?? 'GBP',
      amount: roundToPlaces(vat, 2),
    }
  }

  const vat = totals.resaleVat?.amount
  if (vat == null) return null
  return {
    currency: totals.resaleVat?.currency ?? 'GBP',
    amount: roundToPlaces(vat, 2),
  }
}

// ---------------------------------------------------------------------------
// Delivery price
// ---------------------------------------------------------------------------

/**
 * Computes the full delivery price (inc VAT) from `itemTotals` using
 * `transportTotal` + `transportVat`. For hire products this includes
 * both delivery and collection legs. Returns `null` when transport
 * totals aren't present.
 */
export const getBrendaDeliveryPrice = (
  totals: ItemTotals | null | undefined
): PriceValue | null => {
  const total = totals?.transportTotal?.amount
  if (total == null) return null

  return {
    currency: totals?.transportTotal?.currency ?? 'GBP',
    amount: roundToPlaces(total + (totals?.transportVat?.amount ?? 0), 2),
  }
}

